import React, { useState } from 'react'
import { Route, Switch } from 'react-router-dom'
import CatalogueContainerV2 from '../catalogue/CatalogueContainerV2'
import CatalogueRootV2 from './CatalogueRootV2'
import CategoryBrowseV2 from './CategoryBrowseV2'
import Spinner from '../common/Spinner'
import { routes } from '../../constantes/routes'
import { useSearchEngineInfo, SEARCH_ENGINES } from '../../hooks/useSearchEngine'

/**
 * Decide qué buscador ve cada usuario en el Catálogo.
 *
 * A diferencia del BackOffice, aquí no se puede intercambiar la pantalla
 * entera: el Catálogo es un árbol con rutas anidadas, y sólo está roto el
 * buscador del nivel raíz —el que recorre el árbol con 485 peticiones—.
 * Los niveles profundos filtran en local sobre datos ya cargados y funcionan
 * bien, así que se dejan como están.
 *
 * Por eso el reparto es por ruta y no por pantalla:
 *
 *   /catalogo-v2  exacto      → raíz nueva, con el motor v2
 *   /catalogo-v2/...          → el catálogo de siempre, sin tocar
 *
 * Funciona porque NodesV2 lleva su propio Switch con rutas absolutas: montado
 * en una ruta profunda resuelve él solo al componente que toca.
 *
 * En modo comparación no se apilan los dos catálogos: ambos declararían las
 * mismas rutas y competirían por la URL. Se ofrece un conmutador flotante para
 * ver una raíz o la otra, que además es la comparación que interesa aquí —la
 * experiencia de buscar—. La de datos ya la da /buscador-comparar.
 */

/**
 * Conmutador flotante, sólo en modo comparación.
 *
 * Va fijo en una esquina y no dentro del flujo de la página a propósito: las
 * dos raíces (la nueva y CatalogueContainerV2) traen su propio contenedor
 * .products con 70px de padding superior para despejar la cabecera fija.
 * Insertar una barra encima duplicaría ese hueco en una de las dos.
 */
const ConmutadorFlotante = ({ verLegacy, onCambiar }) => (
  <div
    style={{
      position: 'fixed',
      right: 16,
      bottom: 16,
      zIndex: 1000,
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      fontSize: 12,
      padding: '8px 12px',
      background: '#ffffff',
      border: '1px solid #dce2e8',
      borderLeft: '3px solid #1b5e7e',
      boxShadow: '0 2px 10px rgba(0,0,0,0.12)',
    }}
  >
    <span style={{ color: '#5c6773' }}>Buscador:</span>

    <button
      className='user-input btn-shape'
      style={{ padding: '2px 10px', fontSize: 12, fontWeight: verLegacy ? 400 : 700 }}
      onClick={() => onCambiar(false)}
    >
      v2
    </button>

    <button
      className='user-input btn-shape'
      style={{ padding: '2px 10px', fontSize: 12, fontWeight: verLegacy ? 700 : 400 }}
      onClick={() => onCambiar(true)}
    >
      Actual
    </button>
  </div>
)

const CatalogueSearchHost = (props) => {
  const info = useSearchEngineInfo()
  const [verLegacy, setVerLegacy] = useState(false)

  // Hasta saber qué motor toca no se monta ninguno: montar el equivocado y
  // sustituirlo provoca un parpadeo y, en pantallas que cargan al montarse,
  // peticiones que se tiran.
  if (info.origen === 'cargando') {
    return (
      <div className='products'>
        <Spinner bloque retardado />
      </div>
    )
  }

  if (info.engine === SEARCH_ENGINES.LEGACY) {
    return <CatalogueContainerV2 {...props} />
  }

  const comparando = info.engine === SEARCH_ENGINES.COMPARE
  const raizLegacy = comparando && verLegacy

  return (
    <Switch>
      <Route exact path={routes.CATALOGUE_V2}>
        {raizLegacy ? <CatalogueContainerV2 {...props} /> : <CatalogueRootV2 />}

        {comparando ? (
          <ConmutadorFlotante verLegacy={verLegacy} onCambiar={setVerLegacy} />
        ) : null}
      </Route>

      {/* Desde el nivel de categoría hacia abajo, el subárbol es de v2.
          SIN `exact`: CategoryBrowseV2 declara sus propias rutas hijas y
          éstas las suyas, porque el árbol se anida sin límite. Así la miga
          de pan se acumula en vez de sustituirse. */}
      <Route
        path={`${routes.CATALOGUE_V2}/categoria/:id`}
        component={CategoryBrowseV2}
      />

      {/* Cualquier otro nivel: el catálogo original. */}
      <Route path={routes.CATALOGUE_V2} component={CatalogueContainerV2} />
    </Switch>
  )
}

export default CatalogueSearchHost
